import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import cn from 'classnames';
import { Badge, Button, DropdownMenu, IconButton, Skeleton, Typography } from 'seagull';
import { MoreVertical } from 'seagull/icons';
import { useResponsive } from '~/client-flow/contexts';
import { getRenderedMenuItem } from '~/components/ManagePaymentMethods/ManagePaymentMethods.utils';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import styles from './PaymentMethodPanel.module.scss';
export default function PaymentMethodPanel(props) {
    const { isMobile } = useResponsive();
    const { isSelected, removable, disconnectable, selectable, logo, title, description, footer, onSelect, onRemove, onDisconnect, isProposal = false } = props;
    const RootElement = isMobile ? 'button' : 'div';
    const menuItem = getRenderedMenuItem({
        disconnectable: !!disconnectable,
        removable: !!removable,
        isSelected,
        onDisconnect,
        onRemove
    });
    const { PAYMENT_METHODS_CHANGE_ANIMATION, CLIENT_ONBOARDING_NEW_FLOW } = useFeatureFlags();
    return /*#__PURE__*/ _jsxs(RootElement, {
        className: cn(styles.paymentMethod, {
            [styles.selected]: isSelected,
            [styles.isMobile]: isMobile,
            [styles.fullWidth]: PAYMENT_METHODS_CHANGE_ANIMATION,
            [styles.newFlow]: CLIENT_ONBOARDING_NEW_FLOW,
            [styles.bgAgreementMinisite]: !isProposal,
            [styles.bgClientOnboarding]: isProposal
        }),
        "data-testid": "manage-payment-methods-payment-method",
        onClick: selectable && isMobile ? onSelect : undefined,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: styles.inner,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.logo,
                        children: logo
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles.content,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: CLIENT_ONBOARDING_NEW_FLOW && isMobile ? 'paragraph1' : 'h6',
                                weight: CLIENT_ONBOARDING_NEW_FLOW && isMobile ? 'normal' : 'light',
                                children: title
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                testId: "manage-payment-methods-payment-method-details",
                                variant: CLIENT_ONBOARDING_NEW_FLOW && isMobile ? 'paragraph3' : 'paragraph1',
                                weight: "light",
                                skin: "secondary",
                                children: description
                            })
                        ]
                    }),
                    CLIENT_ONBOARDING_NEW_FLOW ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            !isSelected && selectable && !isMobile && /*#__PURE__*/ _jsx(Button, {
                                className: styles.selectBtn,
                                testId: "payment-method-select-btn",
                                skin: "primaryOutline",
                                size: "small",
                                onClick: onSelect,
                                children: "Select for this agreement"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles.actionsRow,
                                onClick: (e)=>e.stopPropagation(),
                                children: [
                                    isSelected && /*#__PURE__*/ _jsx(Badge, {
                                        className: styles.selectedBadge,
                                        rounded: true,
                                        outlined: true,
                                        skin: "primary",
                                        testId: "manage-payment-methods-payment-method-selected",
                                        children: "Selected"
                                    }),
                                    /*#__PURE__*/ _jsx(DropdownMenu, {
                                        target: /*#__PURE__*/ _jsx(IconButton, {
                                            className: styles.removeBtn,
                                            skin: "transparent",
                                            testId: "payment-method-dropdown",
                                            children: /*#__PURE__*/ _jsx(MoreVertical, {})
                                        }),
                                        children: /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                                            skin: menuItem.skin,
                                            testId: "payment-method-".concat(menuItem.type, "-btn"),
                                            onClick: menuItem.onClick,
                                            disabled: menuItem.disabled,
                                            tooltipProps: menuItem.disabledTooltipText ? {
                                                title: menuItem.disabledTooltipText,
                                                testId: 'disabled-remove-tooltip'
                                            } : undefined,
                                            children: menuItem.text
                                        })
                                    })
                                ]
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx(_Fragment, {
                        children: isSelected ? /*#__PURE__*/ _jsx(Badge, {
                            className: styles.selectedBadge,
                            rounded: true,
                            outlined: true,
                            skin: "primary",
                            testId: "manage-payment-methods-payment-method-selected",
                            children: "Selected"
                        }) : selectable && !isMobile && /*#__PURE__*/ _jsx(Button, {
                            className: styles.selectBtn,
                            testId: "payment-method-select-btn",
                            skin: "primaryOutline",
                            size: "small",
                            onClick: onSelect,
                            children: "Select for this agreement"
                        })
                    })
                ]
            }),
            !CLIENT_ONBOARDING_NEW_FLOW && /*#__PURE__*/ _jsx("div", {
                onClick: (e)=>e.stopPropagation(),
                children: /*#__PURE__*/ _jsx(DropdownMenu, {
                    target: /*#__PURE__*/ _jsx(IconButton, {
                        className: styles.removeBtn,
                        skin: "transparent",
                        testId: "payment-method-dropdown",
                        children: /*#__PURE__*/ _jsx(MoreVertical, {})
                    }),
                    children: /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                        skin: menuItem.skin,
                        testId: "payment-method-".concat(menuItem.type, "-btn"),
                        onClick: menuItem.onClick,
                        disabled: menuItem.disabled,
                        tooltipProps: menuItem.disabledTooltipText ? {
                            title: menuItem.disabledTooltipText,
                            testId: 'disabled-remove-tooltip'
                        } : undefined,
                        children: menuItem.text
                    })
                })
            }),
            footer && /*#__PURE__*/ _jsx("div", {
                className: styles.footer,
                children: footer
            })
        ]
    });
}
export function PaymentMethodPanelSkeleton() {
    const { isMobile } = useResponsive();
    return /*#__PURE__*/ _jsx("div", {
        className: cn(styles.paymentMethod, {
            [styles.isMobile]: isMobile
        }),
        "data-testid": "manage-payment-methods-payment-method-skeleton",
        children: /*#__PURE__*/ _jsxs("div", {
            className: styles.inner,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: styles.logo,
                    children: /*#__PURE__*/ _jsx(Skeleton, {
                        height: 42,
                        width: "100%"
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.content,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "h6",
                            weight: "light",
                            children: /*#__PURE__*/ _jsx(Skeleton, {
                                height: 35,
                                width: "100%",
                                style: {
                                    marginBottom: 8
                                }
                            })
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            testId: "manage-payment-methods-payment-method-details",
                            variant: "paragraph1",
                            weight: "light",
                            skin: "secondary",
                            children: /*#__PURE__*/ _jsx(Skeleton, {
                                height: 24,
                                width: "100%"
                            })
                        })
                    ]
                })
            ]
        })
    });
}
